import { useEffect, useRef, useState, type FormEvent } from "react";
import type { ResizeConfig, ResizeResult, ResizeSchedule, ResizeScheduleInput } from "../hooks/useAdminClaws";
import type { AdminOpenClaw } from "../types/openclaw";
import styles from "./ReassignModal.module.css";

type Props = {
  open: boolean;
  claw: AdminOpenClaw | null;
  onClose: () => void;
  fetchConfig: () => Promise<ResizeConfig | null>;
  fetchSchedule: (tenantId: string) => Promise<ResizeSchedule | null>;
  onSubmit: (tenantId: string, body: ResizeScheduleInput) => Promise<ResizeResult>;
};

const DEFAULT_ZONE = "Australia/Sydney";

// Resilience fallback if GET /resize-config fails; the backend is the source of
// truth. Keep in sync with ztc.resize.allowed-types defaults.
const FALLBACK_CONFIG: ResizeConfig = {
  allowedTypes: ["t3a.small", "t3a.medium", "t3a.large"],
  upDefault: "t3a.large",
  downDefault: "t3a.medium",
};

const SIZE_LABELS: Record<string, string> = {
  "t3a.small": "t3a.small — 2 GB",
  "t3a.medium": "t3a.medium — 4 GB",
  "t3a.large": "t3a.large — 8 GB",
};
const labelFor = (type: string): string => SIZE_LABELS[type] ?? type;

const pad = (n: number): string => String(n).padStart(2, "0");
const minutesToTime = (m: number | null): string =>
  m === null ? "" : `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
const timeToMinutes = (s: string): number | null => {
  if (!s) return null;
  const parts = s.split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isInteger(h) || !Number.isInteger(m)) return null;
  return h * 60 + m;
};

export const ResizeScheduleModal = ({
  open,
  claw,
  onClose,
  fetchConfig,
  fetchSchedule,
  onSubmit,
}: Props): JSX.Element | null => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [config, setConfig] = useState<ResizeConfig>(FALLBACK_CONFIG);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [upSize, setUpSize] = useState<string>(FALLBACK_CONFIG.upDefault);
  const [downSize, setDownSize] = useState<string>(FALLBACK_CONFIG.downDefault);
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [weekendsDown, setWeekendsDown] = useState<boolean>(false);
  const [zone, setZone] = useState<string>(DEFAULT_ZONE);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [lastOutcome, setLastOutcome] = useState<string | null>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && claw !== null && !d.open) {
      d.showModal();
      setServerError(null);
      setSubmitting(false);
      setLoading(true);
      void Promise.all([fetchConfig(), fetchSchedule(claw.tenantId)]).then(([cfg, s]) => {
        const c = cfg && cfg.allowedTypes.length > 0 ? cfg : FALLBACK_CONFIG;
        setConfig(c);
        if (s) {
          setEnabled(s.enabled);
          // A never-scheduled box reports the config default as upSize; seed
          // from its live type so enabling a schedule on a large box keeps it large.
          const live = claw.instanceType && c.allowedTypes.includes(claw.instanceType) ? claw.instanceType : null;
          setUpSize(!s.configured && live ? live : s.upSize);
          setDownSize(s.downSize);
          setStartTime(minutesToTime(s.downStartMinute));
          setEndTime(minutesToTime(s.downEndMinute));
          setWeekendsDown(s.weekendsDown);
          setZone(s.zone);
          setLastOutcome(s.lastOutcome);
        } else {
          // Fetch failed (null). Reset every field to defaults so we never
          // carry over the previously-opened instance's values - otherwise
          // Save would write claw A's schedule onto claw B.
          setEnabled(false);
          setUpSize(c.upDefault);
          setDownSize(c.downDefault);
          setStartTime("");
          setEndTime("");
          setWeekendsDown(false);
          setZone(DEFAULT_ZONE);
          setLastOutcome(null);
        }
        setLoading(false);
      });
    } else if (!open && d.open) {
      d.close();
    }
  }, [open, claw, fetchConfig, fetchSchedule]);

  if (!open || claw === null) return null;

  const handleSubmit = async (evt: FormEvent): Promise<void> => {
    evt.preventDefault();
    if (submitting || loading) return;
    setSubmitting(true);
    setServerError(null);
    const result = await onSubmit(claw.tenantId, {
      enabled,
      upSize,
      downSize,
      downStartMinute: timeToMinutes(startTime),
      downEndMinute: timeToMinutes(endTime),
      weekendsDown,
      zone,
    });
    setSubmitting(false);
    if (result.ok) {
      onClose();
      return;
    }
    setServerError(result.message);
  };

  const busy = submitting || loading;

  return (
    <dialog
      ref={dialogRef}
      className={`${styles.dialog} ${styles.dialogWide}`}
      onCancel={(evt) => {
        evt.preventDefault();
        if (!submitting) onClose();
      }}
    >
      <form method="dialog" className={styles.form} onSubmit={handleSubmit}>
        <header className={styles.header}>
          <h2 className={styles.title}>Resize schedule</h2>
          <p className={styles.subtitle}>
            Automatically downsize{" "}
            <span className={styles.nameToken}>{claw.name}</span> during a
            nightly window and/or weekends, then restore it. Times are in the
            instance's timezone. Each transition causes a brief (1–5 min)
            interruption.
          </p>
        </header>

        <label className={styles.field}>
          <span className={styles.label}>
            <input
              type="checkbox"
              checked={enabled}
              disabled={busy}
              onChange={(e) => setEnabled(e.target.checked)}
            />{" "}
            Enable scheduled resizing
          </span>
        </label>

        <div className={styles.pairRow}>
          <label className={styles.field}>
            <span className={styles.label}>Up size (outside the window)</span>
            <select
              className={styles.input}
              value={upSize}
              disabled={busy}
              onChange={(e) => setUpSize(e.target.value)}
            >
              {config.allowedTypes.map((t) => (
                <option key={t} value={t}>{labelFor(t)}</option>
              ))}
            </select>
            <span className={styles.hint}>
              The size the box returns to when the down-window closes.
            </span>
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Down size</span>
            <select
              className={styles.input}
              value={downSize}
              disabled={busy}
              onChange={(e) => setDownSize(e.target.value)}
            >
              {config.allowedTypes.map((t) => (
                <option key={t} value={t}>{labelFor(t)}</option>
              ))}
            </select>
            <span className={styles.hint}>
              {upSize === downSize ? "Same as the up size — the schedule would never change anything." : "Applied inside the nightly window and on down-weekends."}
            </span>
          </label>
        </div>

        <label className={styles.field}>
          <span className={styles.label}>Nightly down-window (local time)</span>
          <span className={styles.timeRow}>
            <input
              type="time"
              className={styles.input}
              value={startTime}
              disabled={busy}
              onChange={(e) => setStartTime(e.target.value)}
            />
            {" to "}
            <input
              type="time"
              className={styles.input}
              value={endTime}
              disabled={busy}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </span>
          <span className={styles.hint}>
            Leave both blank for no nightly window. End before start wraps
            midnight (e.g. 23:00 to 07:00).
          </span>
        </label>

        <label className={styles.field}>
          <span className={styles.label}>
            <input
              type="checkbox"
              checked={weekendsDown}
              disabled={busy}
              onChange={(e) => setWeekendsDown(e.target.checked)}
            />{" "}
            Downsize all weekend (Sat/Sun)
          </span>
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Timezone (IANA)</span>
          <input
            type="text"
            className={styles.input}
            value={zone}
            disabled={busy}
            onChange={(e) => setZone(e.target.value)}
          />
        </label>

        {lastOutcome ? (
          <span className={styles.hint}>Last reconciler outcome: {lastOutcome}</span>
        ) : null}
        {serverError !== null ? <div className={styles.errorBanner}>{serverError}</div> : null}

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button type="submit" className={styles.primaryBtn} disabled={busy}>
            {submitting ? "Saving…" : "Save schedule"}
          </button>
        </footer>
      </form>
    </dialog>
  );
};
